/**
 * PresentationProgressBar React component
 * 
 * @author    Maurizio Tidei
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from "@material-ui/core";
import { motion } from 'framer-motion'
import {getTextWidth} from "../../../utils";

/**
 * progress bar for presentations
 * Shows the current index in relation to the total count
 *
 * @author Maurizio Tidei
 */
class PresentationProgressBar extends React.Component {

    // TODO: Works fine beginning with about 850px viewport width
    render() {

        const tickVariants = {
            initial: {
                opacity: 0,
                width: "0.05vw",
                backgroundColor: "#888888"
            },
            before: {
                opacity: 1,
                width: "0.05vw",
                backgroundColor: "#888888",
                transition: {
                    ease: "easeInOut", duration: 0.7, delay: 0.3
                }
            },
            after: {
                opacity: 1,
                width: "0.1vw",
                backgroundColor: "#ffffff",
                transition: {
                    ease: "easeInOut", duration: 0.7, delay: 0.3
                }
            }
        };

        const horizontalLineVariants = {
            initial: {
                opacity: 0,
                height: "1px",
                backgroundColor: "#888888",
            },
            before: {
                opacity: 1,
                height: "1px",
                position: "relative",
                top: "0.5px",
                backgroundColor: "#888888",
                transition: {
                    ease: "easeInOut", duration: 0.7, delay: 0.3
                }
            },
            after: i => ({
                opacity: 1,
                height: "0.3vw",
                position: "relative",
                top: "0.15vw",
                backgroundColor: "#ffffff",
                transition: {
                    ease: "easeInOut", duration: 0.7, delay: i * 0.5 / this.props.count
                }
            })
        };

        const lineSegmentWidth = 80 / (this.props.count - 1);

        if (this.props.index >= this.props.count || this.props.count < 2) {
            // No progress bar
            return null;
        }

        const {classes} = this.props;

        let segments = [];
        let afterActiveTick = false;
        for (let i = 0; i < this.props.count; i++) {
            if (!afterActiveTick) {
                afterActiveTick = i === this.props.index;
            }

            let tickVariant = afterActiveTick ? "after" : "before";
            segments.push(
                <motion.div
                    key={"tick" + i}
                    variants={tickVariants}
                    initial={"initial"}
                    animate={tickVariant}
                    className={classes.tick}
                />
            );

            if (i !== this.props.count - 1) {
                segments.push(
                    <motion.div
                        key={"line" + i}
                        style={{width:lineSegmentWidth + "vw"}}
                        variants={horizontalLineVariants}
                        custom={i}
                        initial={"initial"}
                        animate={tickVariant}
                        className={classes.horizontalLine}
                    />
                );
            }
        }

        let labelText = this.props.label || "" + (this.props.index + 1);
        let currentTickPosition = (this.props.index * lineSegmentWidth + (this.props.index+1) * 0.05 - 0.05) + "vw";
        let currentLabelPosition = this.props.index === this.props.count - 1 ?
            "calc(" + currentTickPosition + " - " + getTextWidth(labelText, "2vw Roboto") + "px + 0.1vw)" :
            currentTickPosition;

        return <div>
            <div className={classes.progressBar}>
                <motion.div
                    animate={{left:currentTickPosition}}
                    transition={{ ease: "easeOut", duration: 0.5 }}
                    className={classes.activeTick}
                />
                <motion.div
                    animate={{left: currentLabelPosition}}
                    transition={{ ease: "easeOut", duration: 0.5 }}
                    className={classes.label}
                >
                    {labelText}
                </motion.div>
                {segments}
            </div>
        </div>
    }
}

PresentationProgressBar.propTypes = {
    count: PropTypes.number,
    index: PropTypes.number,
    label: PropTypes.string
};

const styles = theme => ({
    progressBar: {
        width: "100%",
        overflowX: "visible",
        whiteSpace: "nowrap",
        position: "relative"
    },
    tick: {
        height: "1.5vw",
        minWidth: "1px",
        display: "inline-block",
        position: "relative",
        top: "0.75vw"
    },
    activeTick: {
        height: "3vw",
        width: "0.5vw",
        backgroundColor: "white",
        position: "absolute",
        zIndex:99
    },
    label: {
        position: "absolute",
        top: "4vw",
        fontFamily: "Roboto",
        fontSize: "2vw"
    },
    horizontalLine: {
        display: "inline-block",
        top: 0
    }
});

export default withStyles(styles)(PresentationProgressBar);
